import { z } from 'zod';

const vadStartSensitivitySchema = z.enum([
  'START_SENSITIVITY_LOW',
  'START_SENSITIVITY_MEDIUM',
  'START_SENSITIVITY_HIGH',
]);

const vadEndSensitivitySchema = z.enum([
  'END_SENSITIVITY_LOW',
  'END_SENSITIVITY_MEDIUM',
  'END_SENSITIVITY_HIGH',
]);

const thinkingLevelSchema = z.enum(['minimal', 'low', 'medium', 'high']);

/**
 * How custom-tool function responses are scheduled. Consumed by
 * live-api.service.ts, which maps these onto genai's FunctionResponseScheduling.
 */
const toolResponseSchedulingSchema = z.enum(['interrupt', 'when_idle', 'silent']);
export const toolExecutionBehaviorSchema = z.enum(['non_blocking', 'blocking']);
export const toolExecutionOverridesSchema = z.record(
  z.string().min(1).max(128),
  toolExecutionBehaviorSchema,
);
export type ToolExecutionOverrides = z.infer<typeof toolExecutionOverridesSchema>;

export const advancedConfigurationSchema = z.object({
  model: z.string().min(1).optional(),
  gemini_live_backend: z.enum(['ai_studio', 'vertex']).optional(),
  voice_name: z.string().min(1).max(100).optional(),
  temperature: z.number().min(0).max(2).optional(),
  language_code: z
    .string()
    .regex(/^[a-z]{2}-[A-Z]{2}$/, 'Language must be in BCP-47 format like en-GB')
    .optional(),
  /** Words callers say that the recogniser should favour (Live inputAudioTranscription). */
  transcription_vocabulary: z.array(z.string().trim().min(1).max(100)).max(100).optional(),
  /**
   * Parallel transcription model on phone calls: 'log' saves what it heard to the
   * transcript, 'assist' also passes spelled codes to the agent. Missing = off.
   */
  second_ear: z.enum(['off', 'log', 'assist']).optional(),
  thinking_level: thinkingLevelSchema.optional(),
  thinking_budget: z.number().int().min(0).max(32768).optional(),
  include_thoughts: z.boolean().optional(),
  vad_disabled: z.boolean().optional(),
  vad_start_sensitivity: vadStartSensitivitySchema.optional(),
  vad_end_sensitivity: vadEndSensitivitySchema.optional(),
  prefix_padding_ms: z.number().int().min(0).max(5000).optional(),
  silence_duration_ms: z.number().int().min(0).max(10000).optional(),
  enable_affective_dialog: z.boolean().optional(),
  proactive_audio: z.boolean().optional(),
  compression_disabled: z.boolean().optional(),
  trigger_tokens: z.number().int().min(1).max(1_000_000).optional(),
  target_tokens: z.number().int().min(1).max(1_000_000).optional(),
  silence_breaker: z
    .object({
      enabled: z.boolean(),
      timeout_ms: z.number().int().min(2000).max(30000),
      max_attempts: z.number().int().min(1).max(10),
    })
    .optional(),
  grounding_enabled: z.boolean().optional(),
  tool_response_scheduling: toolResponseSchedulingSchema.optional(),
  /** Gemini 3.8 Live declaration behavior; Extended Thinking is async-only. */
  tool_execution_behavior: toolExecutionBehaviorSchema.optional(),
  /** Per-configuration function-name overrides; absence means inherit. */
  tool_execution_overrides: toolExecutionOverridesSchema.optional(),
  // When true, attach the project's Vertex RAG corpus so the agent grounds its
  // answers on uploaded knowledge-base files. Set by the RAG section of the
  // configuration form when files are present.
  rag_enabled: z.boolean().optional(),
  // Which engine runs voice calls. 'genai' (default) is the single-session
  // Live API engine in live-api.service.ts; 'adk' runs the call through the
  // ADK runner (one Live session per agent, native transfers). Experimental.
  voice_engine: z.enum(['genai', 'adk']).optional(),
  // OpenAI GPT-Live only: the Responses model that reasons and calls tools
  // behind the voice model (delegation.responses.model), and its reasoning
  // effort. Ignored by the Gemini engines.
  openai_backend_model: z.string().min(1).max(100).optional(),
  openai_backend_reasoning: z.enum(['none', 'minimal', 'low', 'medium', 'high']).optional(),
  /**
   * What a new web-widget session is primed with from the visitor's earlier
   * calls. 'user_only' (default) replays only what the visitor said — the
   * agent's own earlier lines re-anchored it on stale details (V12, 12 Sep).
   */
  widget_history_priming: z.enum(['user_only', 'full', 'off']).optional(),
  /** Tool name -> tools that must have succeeded earlier in the same session. */
  tool_prerequisites: z.record(z.string(), z.array(z.string().min(1)).max(10)).optional(),
  /** Outbound text guard: deterministic checks before a reply leaves the platform. */
  outbound_guard: z
    .object({
      /** Regex sources; a reply matching any is blocked and regenerated once. */
      deny_patterns: z.array(z.string().min(1).max(500)).max(20).optional(),
      /** Every URL in a reply must come from a tool result this session (or match a prefix below). Off unless set to true. */
      ground_urls: z.boolean().optional(),
      allowed_url_prefixes: z.array(z.string().url()).max(20).optional(),
      fallback_text: z.string().max(1000).optional(),
    })
    .optional(),
});

export type AdvancedConfiguration = z.infer<typeof advancedConfigurationSchema>;
